import React, { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { APPLICATION_STATUS } from '../../../../../constants/event-application-status';
import { TranslucentContainer } from '../../../../../components';
import { venueCancelRetailBooking } from '../../../../../core/vendor-retail/action';

function RetailApplicationsProcessedView({ applications, onStatusChanged, loading }) {
  const dispatch = useDispatch();
  const { currentVenue } = useSelector((store) => store.venues);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const processedApplications = useMemo(
    () =>
      applications?.filter(
        (app) =>
          app.application_status === APPLICATION_STATUS.APPROVED ||
          app.application_status === APPLICATION_STATUS.DENIED,
      ) || [],
    [applications],
  );

  const groupedByVendorAndDate = useMemo(() => {
    const groups = new Map();
    processedApplications.forEach((app) => {
      const vendorId = app.vendor?.id || 'unknown';
      const dateKey = app.booking_date ? moment(app.booking_date).format('YYYY-MM-DD') : 'unknown';
      const key = `${vendorId}|${dateKey}`;
      const startMs = moment(app.start_time, ['HH:mm', 'HH:mm:ss']).valueOf();
      const endMs = moment(app.end_time, ['HH:mm', 'HH:mm:ss']).valueOf();
      if (!groups.has(key)) {
        const vendorFirst = app.vendor?.name || 'Unknown Vendor';
        const vendorLast = app.vendor?.last_name ? ` ${app.vendor.last_name}` : '';
        const vendorNameFull = `${vendorFirst}${vendorLast}`;
        groups.set(key, {
          vendorName: vendorNameFull,
          date: dateKey,
          ranges: [{ start: startMs, end: endMs }],
          total: Number(app.total_price || 0),
          statuses: new Set([app.application_status]),
          contactName: app.contact_name || null,
          contactPhone: app.contact_phone || null,
          contactEmail: app.contact_email || null,
          bookingIds: [app.id], // Track all booking IDs in this group
          hasCancelled: app.has_been_cancelled, // Track if any are cancelled
        });
      } else {
        const g = groups.get(key);
        g.ranges.push({ start: startMs, end: endMs });
        g.total += Number(app.total_price || 0);
        g.statuses.add(app.application_status);
        g.bookingIds.push(app.id);
        if (app.has_been_cancelled) g.hasCancelled = true;
        if (!g.contactName && app.contact_name) g.contactName = app.contact_name;
        if (!g.contactPhone && app.contact_phone) g.contactPhone = app.contact_phone;
        if (!g.contactEmail && app.contact_email) g.contactEmail = app.contact_email;
      }
    });

    const compressRanges = (ranges) => {
      if (ranges.length === 0) return [];
      const sorted = [...ranges].sort((a, b) => a.start - b.start);
      const merged = [sorted[0]];
      for (let i = 1; i < sorted.length; i += 1) {
        const last = merged[merged.length - 1];
        const curr = sorted[i];
        if (curr.start <= last.end) {
          last.end = Math.max(last.end, curr.end);
        } else {
          merged.push({ ...curr });
        }
      }
      return merged;
    };

    return Array.from(groups.values()).map((g) => {
      const merged = compressRanges(g.ranges);
      const timeText = merged
        .map((r) => `${moment(r.start).format('h:mm A')} - ${moment(r.end).format('h:mm A')}`)
        .join(', ');
      let statusLabel = 'mixed';
      let statusColor = 'default';
      if (g.statuses.size === 1) {
        const only = Array.from(g.statuses)[0];
        statusLabel = only;
        statusColor = only === APPLICATION_STATUS.APPROVED ? 'success' : 'error';
      }
      return {
        vendorName: g.vendorName,
        date: g.date,
        timeText,
        total: g.total,
        statusLabel,
        statusColor,
        contactName: g.contactName,
        contactPhone: g.contactPhone,
        contactEmail: g.contactEmail,
        bookingIds: g.bookingIds,
        hasCancelled: g.hasCancelled,
      };
    });
  }, [processedApplications]);

  const handleOpenCancelDialog = (group) => {
    setSelectedGroup(group);
    setCancelDialogOpen(true);
    setCancelReason('');
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedGroup(null);
    setCancelReason('');
  };

  const handleConfirmCancel = () => {
    if (selectedGroup && currentVenue?.id) {
      // Cancel all bookings in this group
      selectedGroup.bookingIds.forEach((bookingId) => {
        dispatch(
          venueCancelRetailBooking(bookingId, currentVenue.id, cancelReason, onStatusChanged),
        );
      });
      handleCloseCancelDialog();
    }
  };

  if (loading) {
    return (
      <Grid container marginTop={5} justifyContent="center">
        <TranslucentContainer>
          <Box textAlign="center" py={8}>
            <Typography variant="h5" color="text.secondary">
              Loading applications...
            </Typography>
          </Box>
        </TranslucentContainer>
      </Grid>
    );
  }

  return (
    <Grid container marginTop={5} justifyContent="center">
      <TranslucentContainer>
        <Paper sx={{ backgroundColor: '#FCE5DD', p: 3, mb: 2, minHeight: '400px' }}>
          <Typography variant="h5" fontWeight="bold" color="#EE7B57" mb={3}>
            Completed Applications
          </Typography>

          {groupedByVendorAndDate.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedByVendorAndDate.map((g) => (
                  <TableRow key={`${g.vendorName}-${g.date}`} hover>
                    <TableCell>
                      {g.hasCancelled ? (
                        <Chip
                          label="cancelled"
                          color="default"
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ) : (
                        <Chip
                          label={g.statusLabel}
                          color={g.statusColor}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{moment(g.date, 'YYYY-MM-DD').format('LL')}</TableCell>
                    <TableCell>{g.timeText}</TableCell>
                    <TableCell align="right">${g.total}</TableCell>
                    <TableCell>
                      <Typography variant="body2" component="div">
                        {g.vendorName}
                      </Typography>
                      {g.contactName && (
                        <Typography variant="body2" color="text.secondary" component="div">
                          {g.contactName}
                        </Typography>
                      )}
                      {g.contactPhone && (
                        <Typography variant="body2" color="text.secondary" component="div">
                          {g.contactPhone}
                        </Typography>
                      )}
                      {g.contactEmail && (
                        <Typography variant="body2" color="text.secondary" component="div">
                          {g.contactEmail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!g.hasCancelled && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleOpenCancelDialog(g)}
                          sx={{ minWidth: '80px' }}>
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">
                No completed applications
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={2}>
                Accepted and rejected applications will appear here
              </Typography>
            </Box>
          )}
        </Paper>
      </TranslucentContainer>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Retail Space Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel this booking?
          </Typography>
          {selectedGroup && (
            <Box mt={2} mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                <strong>Vendor:</strong> {selectedGroup.vendorName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {moment(selectedGroup.date, 'YYYY-MM-DD').format('LL')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Time:</strong> {selectedGroup.timeText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Total:</strong> ${selectedGroup.total}
              </Typography>
            </Box>
          )}
          <TextField
            label="Reason for cancellation (optional)"
            multiline
            rows={3}
            fullWidth
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            variant="outlined"
            margin="normal"
            placeholder="Let the vendor know why this booking is being cancelled..."
          />
          <Typography variant="caption" color="error" display="block" mt={1}>
            This action cannot be undone. The vendor will be notified via email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} color="inherit">
            No, Keep Booking
          </Button>
          <Button onClick={handleConfirmCancel} variant="contained" color="error">
            Yes, Cancel Booking
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

RetailApplicationsProcessedView.propTypes = {
  applications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      application_status: PropTypes.string,
      booking_date: PropTypes.string,
      start_time: PropTypes.string,
      end_time: PropTypes.string,
    }),
  ),
  onStatusChanged: PropTypes.func,
  loading: PropTypes.bool,
};

RetailApplicationsProcessedView.defaultProps = {
  applications: [],
  onStatusChanged: null,
  loading: false,
};

export default RetailApplicationsProcessedView;
